/** Helpers for building unit / room labels in contribution review. */

import type { BuildingUnitEntry } from '../types';

export type { BuildingUnitEntry };

export function parseUnitFloorInput(raw: string): number | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const n = Number(trimmed);
  if (!Number.isInteger(n)) return null;
  return n;
}

export function formatUnitFloorLabel(floor: number): string {
  if (floor < 0) return `Basement ${Math.abs(floor)}`;
  if (floor === 0) return 'Underground';
  if (floor === 1) return 'Ground (1)';
  return `Floor ${floor}`;
}

export function groupBuildingUnitsByFloor(
  units: BuildingUnitEntry[],
): Array<{ floor: number; labels: string[] }> {
  const byFloor = new Map<number, string[]>();
  for (const unit of units) {
    const floor = unit.floor ?? 1;
    const list = byFloor.get(floor) ?? [];
    list.push(unit.label);
    byFloor.set(floor, list);
  }
  return [...byFloor.entries()]
    .sort(([a], [b]) => a - b)
    .map(([floor, labels]) => ({ floor, labels }));
}

export function summarizeBuildingUnitsForReview(units: BuildingUnitEntry[]): string {
  if (units.length === 0) return '';
  const grouped = groupBuildingUnitsByFloor(units);
  const parts = grouped.map(({ floor, labels }) => {
    const preview = labels.slice(0, 5).join(', ');
    const suffix = labels.length > 5 ? ', …' : '';
    return `${formatUnitFloorLabel(floor)}: ${preview}${suffix}`;
  });
  const total = `${units.length} unit${units.length !== 1 ? 's' : ''}`;
  if (parts.length === 1) return `${total} (${parts[0]})`;
  return `${total} — ${parts.join('; ')}`;
}

export function normalizeBuildingUnits(raw: unknown): BuildingUnitEntry[] {
  if (!Array.isArray(raw)) return [];
  const out: BuildingUnitEntry[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const label = String((item as { label?: unknown }).label ?? '').trim();
    const floor = Number((item as { floor?: unknown }).floor);
    if (!label || !Number.isInteger(floor)) continue;
    out.push({ label, floor });
  }
  return out;
}
